/**
 * Follow-Up OTP Verification Page
 *
 * Verifies user identity via OTP before allowing access to follow-up form.
 * Features:
 * - 6-digit OTP input
 * - Countdown timer (5 minutes)
 * - Resend OTP functionality
 * - Retry limit display (3 attempts)
 * - Error handling with clear messages
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFollowUp } from '../context/FollowUpContext';
import { useTenant } from '../context/TenantContext';
import { requestOtp, verifyOtp } from '../services/followupService';
import defaultLogo from '../assets/logo.png';
import lockIcon from '../assets/icons/lock-icon.svg';
import logger from '../utils/logger';
import activityLogger from '../utils/activityLogger';
import '../styles/formPage.css';
import '../styles/followup.css';
import { secureSessionStorage } from '../utils/secure-session';

const OTP_STEP = [2, 'OTP Verification'];

const FollowUpOTPVerification = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const {
    updatedFields,
    previousData,
    setOtpVerified,
    setOtpSessionId,
    isExistingUser,
    phoneHint,
    loadSubmissionData,
  } = useFollowUp();

  // ═══════════════════════════════════════════════════
  // VIEW STATE MACHINE — drives the entire render
  // Only ONE view is rendered at a time.
  // 'method_select' → 'otp_input' → 'success' | 'lockout' | 'permanent_lock'
  // ═══════════════════════════════════════════════════
  const [view, setView] = useState('method_select');
  const [lockoutData, setLockoutData] = useState(null); // { lockedUntil, stage, warning }

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null); // null until server tells us
  const [canResend, setCanResend] = useState(false);
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [loadingMethod, setLoadingMethod] = useState(null);

  // Method selection state
  const [otpMethod, setOtpMethod] = useState(null);
  const [otpSent, setOtpSent] = useState(false);

  // Legacy lockout state — kept for backward compat, driven by view
  const [lockoutState, setLockoutState] = useState(null);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);

  const inputRefs = useRef([]);
  const logoToDisplay = tenant?.logoUrl || defaultLogo;
  const email = updatedFields?.email;

  // Phone number - check database first (previousData), fallback to current form (updatedFields)
  const phoneNumber = previousData?.phonenumber || previousData?.phone || previousData?.PhoneNumber
    || updatedFields?.phonenumber || updatedFields?.phone || '';

  const maskPhone = (num) => {
    if (!num) return '';
    const digits = String(num).replace(/\D/g, '');
    if (digits.length <= 4) return '****';
    return digits.slice(0, 3) + '*'.repeat(digits.length - 5) + digits.slice(-2);
  };

  // Before the code is verified we only have the server's masked phone (phoneHint).
  const phoneDisplay = phoneHint || maskPhone(phoneNumber);
  const hasPhone = !!(phoneHint || phoneNumber);

  // After a correct code: keep the session, load the patient's previous answers with it, then continue.
  const finishVerification = async (sessionId) => {
    setOtpSessionId(sessionId);
    await secureSessionStorage.setItem('otp_session_id', sessionId).catch(() => {});
    await secureSessionStorage.setItem('otp_verified_email', email).catch(() => {});
    // Show the success check for at least a second, as before, while the answers load.
    const minDelay = new Promise((resolve) => setTimeout(resolve, 1000));
    try {
      await loadSubmissionData(email);
    } catch (err) {
      logger.error('[OTP VERIFY] Could not load previous answers:', err);
    }
    await minDelay;
    navigate('/new-issues');
  };

  // Track if user is locked (used to disable method buttons)
  const [isLockedOnMount, setIsLockedOnMount] = useState(false);

  // On mount: check server for lockout status (survives reload/navigation)
  // Show method_select first, then auto-switch to lockout after 2 seconds
  useEffect(() => {
    if (!email) return;
    const checkStatus = async () => {
      try {
        const { checkOtpStatus } = await import('../services/followupService');
        const status = await checkOtpStatus(email);
        if (status.state === 'locked') {
          // Show method select briefly with disabled buttons, then switch to lockout
          setIsLockedOnMount(true);
          setView('method_select');
          setTimeout(() => {
            setLockoutData({
              lockedUntil: status.locked_until,
              remainingMs: status.remaining_ms,
              stage: status.lockout_stage,
            });
            setView('lockout');
          }, 2000);
        } else if (status.state === 'permanent_lock') {
          setIsLockedOnMount(true);
          setView('method_select');
          setTimeout(() => {
            setLockoutData({ permanent: true });
            setView('permanent_lock');
          }, 2000);
        } else if (status.state === 'otp_active') {
          setAttemptsRemaining(status.attempts_remaining);
          setView('otp_input');
        }
      } catch (e) { /* non-critical */ }
    };
    checkStatus();
  }, [email]);

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      navigate('/');
    }
  }, [email, navigate]);

  // Reset OTP verified state when landing on this page (user came back or navigated here)
  useEffect(() => {
    setOtpVerified(false);
    setOtpSessionId(null);
    secureSessionStorage.removeItem('otp_session_id');
    secureSessionStorage.removeItem('otp_verified_email');
  }, []);

  // Send OTP with selected method
  const sendOtp = async (method, resend = false) => {
    setIsRequestingOtp(true);
    setLoadingMethod(method);
    setError(null);
    setSuccess(false);
    setOtpVerified(false);
    if (resend) setOtp(['', '', '', '', '', '']);

    try {
      // The server texts the number on the patient's intake; it ignores any number sent from here.
      const result = await requestOtp(email, tenant?.id, method, null);
      if (result.success) {
        activityLogger.stepStarted(...OTP_STEP);
        setCountdown(result.expiresIn || 300);
        setAttemptsRemaining(result.attemptsRemaining ?? result.maxAttempts ?? 3);
        setCanResend(false);
        setOtpSent(true);
        setOtpMethod(method);
        setError(null);
        if (resend) {
          setOtp(['', '', '', '', '', '']);
          setTimeout(() => inputRefs.current[0]?.focus(), 100);
        }
      } else if (result.retryAfter || result.permanent) {
        // Account locked — keep fields disabled
        activityLogger.stepSaveError(...OTP_STEP, { message: `OTP request blocked (${method}): ${result.message || 'account locked'}` });
        setCountdown(result.retryAfter || 0);
        setAttemptsRemaining(0);
        setCanResend(false);
        setOtpSent(true);
        setOtpMethod(method);
        setError(result.message);
      } else {
        // Send failed (e.g. SMS region not enabled) - stay on selection, show error
        activityLogger.stepSaveError(...OTP_STEP, { message: `OTP send failed (${method}): ${result.message || 'unknown reason'}` });
        setError(result.message || `Failed to send OTP via ${method}. Please try the other method.`);
        setOtpSent(false);
        setOtpMethod(null);
      }
    } catch (err) {
      if (err instanceof TypeError) activityLogger.networkError(...OTP_STEP, err);
      else activityLogger.stepSaveError(...OTP_STEP, { message: `OTP send failed (${method}): ${err.message}` });
      setError(err.message || 'Failed to send OTP. Please try again.');
      setOtpSent(false);
      setOtpMethod(null);
    } finally {
      setIsRequestingOtp(false);
      setLoadingMethod(null);
    }
  };

  const handleMethodSelect = (method) => {
    // Block OTP send if user is locked
    if (isLockedOnMount) return;
    setError(null);
    sendOtp(method);
  };

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  // Format countdown as MM:SS
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle OTP input change
  const handleChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Only take last character
    setOtp(newOtp);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5 && newOtp.every(d => d)) {
      handleVerify(newOtp.join(''));
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);

      // Focus appropriate input
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();

      // Auto-submit if complete
      if (pastedData.length === 6) {
        handleVerify(pastedData);
      }
    }
  };

  // Verify OTP
  const handleVerify = async (otpCode = otp.join('')) => {
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await verifyOtp(email, otpCode, tenant?.id);

      // Handle response based on status field (server source of truth)
      logger.log('[OTP VERIFY] Verification completed');
      const status = result.status || (result.verified ? 'success' : 'wrong_otp');

      switch (status) {
        case 'success':
          activityLogger.stepCompleted(...OTP_STEP);
          setSuccess(true);
          setOtpVerified(true);
          await finishVerification(result.sessionId);
          return;

        case 'locked':
          activityLogger.stepSaveError(...OTP_STEP, { message: 'Locked out after too many wrong codes', status });
          // FULL VIEW REPLACEMENT — unmount OTP input entirely
          setLockoutData({
            lockedUntil: result.locked_until,
            remainingMs: result.remaining_ms,
            stage: result.stage || result.lockout_stage,
            warning: result.warning,
          });
          setView('lockout');
          // Clear OTP state completely (cleanup)
          setOtp(['', '', '', '', '', '']);
          setError(null);
          setCountdown(0);
          return;

        case 'permanent_lock':
          activityLogger.stepSaveError(...OTP_STEP, { message: 'Account permanently locked', status });
          setLockoutData({ permanent: true });
          setView('permanent_lock');
          setOtp(['', '', '', '', '', '']);
          setError(null);
          setCountdown(0);
          return;

        case 'wrong_otp':
          // Still has attempts remaining — show inline error
          activityLogger.stepSaveError(...OTP_STEP, { message: 'Wrong code entered', status });
          setAttemptsRemaining(result.remaining_attempts ?? result.attemptsRemaining ?? attemptsRemaining - 1);
          setError(result.message || 'Invalid code.');
          setOtp(['', '', '', '', '', '']);
          inputRefs.current[0]?.focus();
          return;

        case 'otp_expired':
          activityLogger.stepSaveError(...OTP_STEP, { message: 'Code expired', status });
          setError('Code expired. Please request a new one.');
          setCanResend(true);
          setCountdown(0);
          setOtp(['', '', '', '', '', '']);
          return;

        default:
          // Fallback — treat as wrong OTP or check for legacy response format
          if (result.verified) {
            activityLogger.stepCompleted(...OTP_STEP);
            setSuccess(true);
            setOtpVerified(true);
            await finishVerification(result.sessionId);
          } else {
            activityLogger.stepSaveError(...OTP_STEP, { message: result.message || 'Code rejected', status });
            setError(result.message || 'Invalid code.');
            setOtp(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
          }
      }
    } catch (err) {
      if (err instanceof TypeError) activityLogger.networkError(...OTP_STEP, err);
      else activityLogger.stepSaveError(...OTP_STEP, { message: `OTP verify failed: ${err.message}` });
      setError('Failed to verify OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    if (!canResend || isRequestingOtp) return;
    sendOtp(otpMethod, true);
  };

  // Switch method (go back to selection)
  const handleSwitchMethod = () => {
    setOtpSent(false);
    setOtpMethod(null);
    setOtp(['', '', '', '', '', '']);
    setError(null);
    setCountdown(0);
    setCanResend(false);
  };

  // Go back
  const handleBack = () => {
    navigate('/');
  };

  return (
    <div>
      {/* Logo */}
      <div className="logo-img d-flex-center">
        <div style={{ textAlign: 'center' }}>
          <img
            src={logoToDisplay}
            alt={tenant?.brandName || 'Logo'}
            style={{ width: '200px', height: 'auto', marginTop: '10px' }}
          />
        </div>
      </div>

      {/* OTP Verification Card */}
      <div className="form-container" style={{ maxWidth: '450px', margin: '40px auto', padding: '10px' }}>
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '40px 30px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}>
          {/* Icon */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#e8f5e9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              transition: 'all 0.3s ease',
            }}>
              {success ? (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4caf50" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                <img src={lockIcon} alt="Lock" width="32" height="32" />
              )}
            </div>
          </div>

          {/* Title */}
          <h2 style={{
            textAlign: 'center',
            margin: '0 0 8px 0',
            fontSize: '24px',
            fontWeight: '600',
            color: '#333',
          }}>
            {success ? 'Verified!' : !otpSent ? 'Security verification' : 'Verify your identity'}
          </h2>

          {/* Subtitle */}
          <p style={{
            textAlign: 'center',
            margin: '0 0 32px 0',
            fontSize: '14px',
            color: '#666',
            lineHeight: '1.5',
          }}>
            {success ? (
              'Redirecting to your form...'
            ) : !otpSent ? (
              'Choose how to receive your code:'
            ) : (
              <>
                We've sent a 6-digit code to<br />
                <strong style={{ color: '#333' }}>
                  {otpMethod === 'phone' ? phoneDisplay : email}
                </strong>
              </>
            )}
          </p>

          {/* Method Selection */}
          {/* VIEW: METHOD SELECT — only when not locked, not success, not otp sent */}
          {view !== 'lockout' && view !== 'permanent_lock' && !success && !otpSent && (
            <>
              {/* Locked notice */}
              {isLockedOnMount && (
                <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '13px', color: '#92400e', textAlign: 'center' }}>
                  Account is locked. Redirecting to lockout timer...
                </div>
              )}

              {/* Email Option */}
              <button
                onClick={() => handleMethodSelect('email')}
                disabled={isRequestingOtp || isLockedOnMount}
                style={{
                  width: '100%',
                  opacity: isLockedOnMount ? 0.5 : 1,
                  padding: '16px 20px',
                  marginBottom: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '10px',
                  backgroundColor: '#fff',
                  cursor: isRequestingOtp ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-primary, #1976d2)'; e.currentTarget.style.backgroundColor = '#f8f9ff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.backgroundColor = '#fff'; }}
              >
                <div style={{
                  width: '44px', height: '44px', borderRadius: '50%',
                  backgroundColor: 'var(--color-primary-light, #e3f2fd)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary, #1976d2)" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <div style={{ textAlign: 'left', flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#333', fontSize: '15px' }}>Email</div>
                  <div style={{ color: '#666', fontSize: '13px' }}>{email}</div>
                </div>
                {loadingMethod === 'email' && (
                  <span style={{
                    marginLeft: 'auto',
                    width: '18px', height: '18px',
                    border: '2px solid var(--color-primary, #1976d2)', borderTopColor: 'transparent',
                    borderRadius: '50%', animation: 'spin 1s linear infinite', flexShrink: 0,
                  }} />
                )}
              </button>

              {/* Phone Option */}
              {hasPhone ? (
                <button
                  onClick={() => handleMethodSelect('phone')}
                  disabled={isRequestingOtp}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    marginBottom: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    backgroundColor: '#fff',
                    cursor: isRequestingOtp ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-primary, #1976d2)'; e.currentTarget.style.backgroundColor = '#f8f9ff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.backgroundColor = '#fff'; }}
                >
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '50%',
                    backgroundColor: '#e8f5e9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4caf50" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </div>
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#333', fontSize: '15px' }}>Phone (SMS)</div>
                    <div style={{ color: '#666', fontSize: '13px' }}>{phoneDisplay}</div>
                  </div>
                  {loadingMethod === 'phone' && (
                    <span style={{
                      marginLeft: 'auto',
                      width: '18px', height: '18px',
                      border: '2px solid #4caf50', borderTopColor: 'transparent',
                      borderRadius: '50%', animation: 'spin 1s linear infinite', flexShrink: 0,
                    }} />
                  )}
                </button>
              ) : (
                <div style={{
                  width: '100%',
                  padding: '16px 20px',
                  marginBottom: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '10px',
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  opacity: 0.5,
                }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '50%',
                    backgroundColor: '#eee',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: '600', color: '#999', fontSize: '15px' }}>Phone (SMS)</div>
                    <div style={{ color: '#999', fontSize: '13px' }}>No phone number on file</div>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div style={{
                  backgroundColor: '#ffebee', border: '1px solid #ffcdd2',
                  borderRadius: '8px', padding: '12px 16px', marginBottom: '20px',
                  color: '#c62828', fontSize: '14px', textAlign: 'center',
                }}>
                  {error}
                </div>
              )}

              {/* Back Button */}
              <div style={{ textAlign: 'center', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
                <button
                  onClick={handleBack}
                  style={{
                    background: 'none', border: 'none', color: '#666', fontSize: '14px',
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  Back to login
                </button>
              </div>
            </>
          )}

          {/* VIEW: LOCKOUT — completely replaces OTP UI. No OTP input, timer, or buttons rendered. */}
          {(view === 'lockout' || view === 'permanent_lock') && lockoutData && (
            <LockoutScreen
              lockoutState={{ ...lockoutData, locked: true, permanent: view === 'permanent_lock' }}
              onUnlock={() => {
                // Lockout expired — return to method select to request new OTP
                const stage = lockoutData.stage || 1;
                setLockoutData(null);
                setView('otp_input');
                setCanResend(true);
                setAttemptsRemaining(stage >= 2 ? 1 : stage >= 1 ? 1 : 3);
                setError(null);
                setOtpSent(true); // So resend button is visible
              }}
              onBack={() => navigate('/')}
            />
          )}

          {/* VIEW: OTP INPUT — only rendered when NOT locked */}
          {view !== 'lockout' && view !== 'permanent_lock' && !success && otpSent && (
            <>
              {/* OTP Input */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '24px',
              }}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    disabled={isLoading || attemptsRemaining <= 0}
                    style={{
                      width: '48px',
                      height: '56px',
                      textAlign: 'center',
                      fontSize: '24px',
                      fontWeight: '600',
                      border: error ? '2px solid #f44336' : '2px solid #e0e0e0',
                      borderRadius: '8px',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      backgroundColor: attemptsRemaining <= 0 ? '#f5f5f5' : '#fff',
                      padding: '10px',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--color-primary, #1976d2)';
                      e.target.style.boxShadow = '0 0 0 3px var(--color-primary-light, rgba(25,118,210,0.1))';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = error ? '#f44336' : '#e0e0e0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                ))}
              </div>

              {/* Error Message */}
              {error && (
                <div style={{
                  backgroundColor: '#ffebee',
                  border: '1px solid #ffcdd2',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  color: '#c62828',
                  fontSize: '14px',
                  textAlign: 'center',
                }}>
                  {error}
                </div>
              )}

              {/* Timer and Attempts */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                padding: '12px 16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                fontSize: '14px',
              }}>
                <div style={{ color: countdown > 0 ? '#666' : '#f44336' }}>
                  {countdown > 0 ? (
                    <>
                      <span style={{ marginRight: '4px' }}>⏱</span>
                      Expires in {formatCountdown(countdown)}
                    </>
                  ) : (
                    'Code expired'
                  )}
                </div>
                {attemptsRemaining !== null && (
                  <div style={{
                    color: attemptsRemaining <= 1 ? '#f44336' : '#666',
                    fontWeight: attemptsRemaining <= 1 ? '600' : '400',
                  }}>
                    {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} left
                  </div>
                )}
              </div>

              {/* Verify Button */}
              <button
                onClick={() => handleVerify()}
                disabled={isLoading || otp.some(d => !d) || attemptsRemaining <= 0}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#fff',
                  backgroundColor: 'var(--color-primary, #4a7a2c)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isLoading || otp.some(d => !d) || attemptsRemaining <= 0 ? 'not-allowed' : 'pointer',
                  opacity: isLoading || otp.some(d => !d) || attemptsRemaining <= 0 ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  marginBottom: '16px',
                }}
              >
                {isLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid #fff',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }} />
                    Verifying...
                  </span>
                ) : (
                  'Verify code'
                )}
              </button>

              {/* Resend Link */}
              <div style={{ textAlign: 'center' }}>
                {canResend ? (
                  <button
                    onClick={handleResend}
                    disabled={isRequestingOtp}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-primary, #1976d2)',
                      fontSize: '14px',
                      cursor: isRequestingOtp ? 'not-allowed' : 'pointer',
                      textDecoration: 'underline',
                      opacity: isRequestingOtp ? 0.6 : 1,
                    }}
                  >
                    {isRequestingOtp ? 'Sending...' : 'Resend Code'}
                  </button>
                ) : null}
              </div>

              {/* Switch method + Back */}
              <div style={{ textAlign: 'center', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                  onClick={handleBack}
                  style={{
                    background: 'none', border: 'none', color: '#666', fontSize: '14px',
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  Back to login
                </button>
                {/* Hide "Try Phone" if no phone on file, always show "Try Email" */}
                {(otpMethod === 'phone' || (otpMethod === 'email' && hasPhone)) && (
                  <button
                    onClick={handleSwitchMethod}
                    style={{
                      background: 'none', border: 'none', color: 'var(--color-primary, #4a7a2c)',
                      fontSize: '14px', cursor: 'pointer',
                    }}
                  >
                    Try {otpMethod === 'email' ? 'Phone' : 'Email'} instead
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

/**
 * LockoutScreen — replaces OTP input when lockout is active.
 * No OTP input, no Resend button, no Verify button.
 * Only shows countdown timer + warning.
 */
const LockoutScreen = ({ lockoutState, onUnlock, onBack }) => {
  const [remaining, setRemaining] = useState(lockoutState.remainingMs || 0);

  useEffect(() => {
    if (lockoutState.permanent) return;
    if (!lockoutState.lockedUntil) return;

    const update = () => {
      const now = Date.now();
      const left = Math.max(0, lockoutState.lockedUntil - now);
      setRemaining(left);
      if (left <= 0) onUnlock();
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [lockoutState, onUnlock]);

  const formatTime = (ms) => {
    const totalSec = Math.ceil(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (lockoutState.permanent) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fee2e2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#dc2626', margin: '0 0 12px' }}>Account Locked</h2>
        <p style={{ color: '#666', fontSize: '14px', lineHeight: 1.6, margin: '0 0 24px' }}>
          Your account has been permanently locked due to multiple failed attempts. Please contact the office to regain access.
        </p>
        <button onClick={onBack} style={{ padding: '10px 24px', background: 'none', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151', fontFamily: 'inherit' }}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', padding: '20px' }}>
      <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fef3c7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
      </div>
      <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#92400e', margin: '0 0 8px' }}>Too Many Failed Attempts</h2>
      <p style={{ color: '#666', fontSize: '14px', lineHeight: 1.6, margin: '0 0 16px' }}>
        {lockoutState.stage === 1
          ? "You've entered 3 incorrect codes. Please wait before trying again."
          : "You've entered another incorrect code. Please wait before trying again."}
      </p>

      {/* Countdown */}
      <div style={{ fontSize: '36px', fontWeight: 700, color: '#92400e', margin: '16px 0', fontVariantNumeric: 'tabular-nums' }}>
        {formatTime(remaining)}
      </div>

      {lockoutState.warning && (
        <p style={{ color: '#dc2626', fontSize: '13px', fontWeight: 500, margin: '0 0 16px', background: '#fee2e2', borderRadius: '6px', padding: '8px 12px', display: 'inline-block' }}>
          {lockoutState.warning}
        </p>
      )}

      <div style={{ marginTop: '24px' }}>
        <button onClick={onBack} style={{ padding: '10px 24px', background: 'none', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151', fontFamily: 'inherit' }}>
          Back to login
        </button>
      </div>
    </div>
  );
};

export default FollowUpOTPVerification;